import { useState, useEffect, useCallback } from "react";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { testimonialsData } from "@/mocks/homeData";

export default function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const total = testimonialsData.testimonials.length;
  const { ref, isVisible } = useScrollAnimation(0.1);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(goNext, 6000);
    return () => clearInterval(timer);
  }, [goNext]);

  const testimonial = testimonialsData.testimonials[activeIndex];

  return (
    <section id="testimonials" className="w-full py-16 md:py-24 bg-background-100">
      <div
        ref={ref}
        className={`w-full px-4 md:px-6 lg:px-10 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <span className="text-xs md:text-sm font-semibold text-primary-500 uppercase tracking-widest">
            {testimonialsData.label}
          </span>
          <h2 className="font-heading font-bold text-xl md:text-3xl lg:text-4xl text-foreground-950 mt-3 mb-4 leading-tight">
            {testimonialsData.headline}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl mx-auto">
            {testimonialsData.subheadline}
          </p>
        </div>

        <div className="max-w-4xl mx-auto relative">
          <button
            onClick={goPrev}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full bg-background-50 border border-background-200/70 items-center justify-center hover:border-primary-300 transition-all cursor-pointer z-10"
            aria-label="Previous testimonial"
          >
            <i className="ri-arrow-left-s-line text-lg text-foreground-700" />
          </button>
          <button
            onClick={goNext}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full bg-background-50 border border-background-200/70 items-center justify-center hover:border-primary-300 transition-all cursor-pointer z-10"
            aria-label="Next testimonial"
          >
            <i className="ri-arrow-right-s-line text-lg text-foreground-700" />
          </button>

          <div className="bg-background-50 rounded-xl p-6 md:p-10 border border-background-200/70">
            <div className="flex justify-center gap-0.5 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <i key={star} className="ri-star-fill text-accent-500" />
              ))}
            </div>

            <blockquote className="font-heading text-base md:text-xl lg:text-2xl text-foreground-900 leading-relaxed mb-8 text-center text-balance">
              &ldquo;{testimonial.quote}&rdquo;
            </blockquote>

            <div className="flex flex-col items-center">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                <span className="font-heading font-semibold text-primary-600 text-base md:text-lg">
                  {testimonial.initials}
                </span>
              </div>
              <div className="font-semibold text-foreground-950 text-sm md:text-base text-center">
                {testimonial.author}
              </div>
              <div className="text-xs text-foreground-500 mt-0.5">
                {testimonial.company} — {testimonial.industry}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-6">
            {testimonialsData.testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`transition-all rounded-full cursor-pointer ${
                  idx === activeIndex
                    ? "w-7 h-2.5 bg-primary-500"
                    : "w-2.5 h-2.5 bg-background-300 hover:bg-background-400"
                }`}
                aria-label={`Testimonial ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}